import DoctornowLogo from '@/components/atom/icon/DoctornowLogo';
import {
  HOSPITAL_SUBMENUS,
  MAGAZINE_SUBMENUS,
  NON_BENEFIT_SUBMENUS,
  QNA_SUBMENUS,
  TELEMEDICINE_SUBMENUS,
} from '@/constants/common';
import { MASS_EVENT_END_DATE_TIME } from '@/constants/etc';
import { isBeforeTargetDate } from '@/utils/date';
import { cn } from '@/utils/style';
import { throttle } from 'lodash';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

const styles = {
  topItemWrapper: cn(['h-[80px] flex items-center px-[8px]']),
  listItemWrapper: cn(['flex flex-col']),
  subMenusWrapper: cn(['pt-[20px] pb-[40px] flex flex-col gap-[12px]']),
};

interface SubMenu {
  text: string;
  url: string;
}

interface RouteInfo {
  subMenus: SubMenu[];
}

const routes: Record<string, RouteInfo> = {
  '비대면 진료': {
    subMenus: TELEMEDICINE_SUBMENUS,
  },
  '병원 찾기': {
    subMenus: HOSPITAL_SUBMENUS,
  },
  '가격 정보': {
    subMenus: NON_BENEFIT_SUBMENUS,
  },
  '건강 정보': {
    subMenus: MAGAZINE_SUBMENUS,
  },
  '실시간 의료 상담': {
    subMenus: QNA_SUBMENUS,
  },
};

const routerKeys = Object.keys(routes);
const HEADER_HEIGHT = 80;

const MainHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const isBeforeMassEventEndDateTime = isBeforeTargetDate(MASS_EVENT_END_DATE_TIME);
  const shouldShowMassEventTextColor = isBeforeMassEventEndDateTime;

  const getRouteSubMenus = (key: string) => {
    return routes[key].subMenus || [];
  };

  const onClickAppDownload = () => {
    window.open('https://abr.ge/0os6ry');
  };

  useEffect(() => {
    const handleScroll = throttle(() => {
      const isScrolled = window.scrollY > HEADER_HEIGHT;
      setScrolled(isScrolled);
    }, 100);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Header
      scrolled={scrolled}
      className={cn(['text-gray-700 hover:text-gray-700', shouldShowMassEventTextColor && 'text-white'])}
    >
      <section className={'relative z-10 mx-auto flex w-full max-w-[1080px]'}>
        <div className={cn([styles.topItemWrapper, 'mr-[48px]'])}>
          <DoctornowLogo />
        </div>
        <Nav>
          <ul className={'flex gap-[48px]'}>
            {routerKeys.map((routerKey) => {
              const routeSubMenus = getRouteSubMenus(routerKey);

              return (
                <li key={`route-${routerKey}`} className={'flex flex-col'}>
                  <div className={cn([styles.topItemWrapper, 'top-item'])}>
                    <div
                      className={cn(
                        'typography-ui-17sb flex h-full items-center hover:text-primary-600',
                        scrolled && 'text-gray-700'
                      )}
                    >
                      {routerKey}
                    </div>
                  </div>
                  <ul className={styles.subMenusWrapper}>
                    {routeSubMenus.map((routerSubMenu) => {
                      return (
                        <li key={`subMenu-${routerSubMenu.text}`}>
                          <Link href={routerSubMenu.url}>
                            <a
                              className={cn([
                                'typography-ui-17r flex h-[36px] items-center rounded-[4px] px-[8px] py-[6px] text-gray-600',
                                'hover:typography-ui-17m hover:bg-gray-30 hover:text-gray-700',
                              ])}
                            >
                              {routerSubMenu.text}
                            </a>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
        </Nav>
        <div className={'ml-auto py-[16px]'}>
          <button
            className={'ty typography-ui-17sb h-[48px] rounded-[12px] bg-primary-600 px-[16px] text-white'}
            onClick={onClickAppDownload}
          >
            앱 다운로드
          </button>
        </div>
      </section>
    </Header>
  );
};

export default MainHeader;

interface HeaderProps {
  scrolled: boolean;
}

const Header = styled.header<HeaderProps>`
    position: fixed;
    top: 0;
    z-index: 200;
    width: 100%;
    overflow-y: hidden;
    height: 80px;
    background-image: ${({ theme, scrolled }) =>
      scrolled
        ? `linear-gradient(to bottom, ${theme.color.white} 80px, ${theme.color.gray10} 80px, ${theme.color.gray10} 100%)`
        : 'none'};
    &:hover {
        height: auto;
        background-image: ${({ theme }) => `linear-gradient(to bottom, ${theme.color.white} 80px, ${theme.color.gray10} 80px, ${theme.color.gray10} 100%)`};
    }
`;

const Nav = styled.nav`
  & li:before, & li:after {
    display: none;
    float: none;
  }
`;
